<template>
  <div class="schedule-time-range flex items-center gap-1 shrink-0">
    <div class="w-[99px] shrink-0 min-w-0">
      <combobox
        v-model="timeFromLabel"
        :items="timeFromOptions"
        :is-multiple="false"
        allow-custom-value
        apply-on-change
        :error="isTimeFromInvalid"
        class="w-full! max-w-full! min-w-0! [&_.wui-combobox]:w-full! [&_.wui-combobox]:min-w-0!"
        placeholder="—"
        data-test="schedule-time-from"
        @search="onTimeFromSearch"
      />
    </div>
    <span>–</span>
    <div class="w-[99px] shrink-0 min-w-0">
      <combobox
        v-model="timeToLabel"
        :items="timeToOptions"
        :is-multiple="false"
        allow-custom-value
        apply-on-change
        :error="isTimeToInvalid"
        class="w-full! max-w-full! min-w-0! [&_.wui-combobox]:w-full! [&_.wui-combobox]:min-w-0!"
        placeholder="—"
        data-test="schedule-time-to"
        @search="onTimeToSearch"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SelectItem, WuiCombobox } from '@wui/common-library'
import dayjs from 'dayjs'
import { type Component, computed, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue'

import { getProfileTimezone } from '@/shared/composables/timezone'

import { INITIAL_TIME, TIME_SLOT_STEP_MS } from '../consts'
import {
  buildTimeSlotOptions,
  ensureTimeToDiffers,
  getDefaultScheduleTimeRange,
  getNextTimeSlotOffset,
  isScheduleTimeUnset,
  isValidTimeLabel,
  msToTimeLabel,
  shiftEndKeepingDuration,
  timeLabelToMs,
} from '../lib/schedule-time'

const Combobox = WuiCombobox as Component

const props = defineProps<{
  isTodaySelected?: boolean
}>()

// Use explicit profile timezone instead of relying on `dayjs.tz.setDefault()`.
const now = ref(dayjs().tz(getProfileTimezone()))

const allTimeSlots = buildTimeSlotOptions(TIME_SLOT_STEP_MS)
const allTimesForSelect: SelectItem<string>[] = allTimeSlots.map((slot) => ({
  label: slot.label,
  value: slot.label,
}))

const timeFrom = defineModel<number>('timeFrom', { default: INITIAL_TIME })
const timeTo = defineModel<number>('timeTo', { default: INITIAL_TIME })
const isTimeInvalid = defineModel<boolean>('isTimeInvalid', { default: false })

const isTimeFromInvalid = ref(false)
const isTimeToInvalid = ref(false)

watch([isTimeFromInvalid, isTimeToInvalid], ([fromInvalid, toInvalid]) => {
  isTimeInvalid.value = fromInvalid || toInvalid
}, { immediate: true })

const shouldShowTimeError = (draft: string): boolean => {
  const trimmed = draft.trim()
  if (!trimmed) {
    return false
  }
  // Пока набирают «18:» — не ругаемся; ошибка после попытки ввести минуты.
  if (/^\d{1,2}:?$/.test(trimmed)) {
    return false
  }
  return !isValidTimeLabel(trimmed)
}

const parseComboboxValue = (value: string | number | undefined | null): number | null => {
  if (value === '' || value === undefined || value === null) {
    return INITIAL_TIME
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  const asMs = Number(value)
  if (Number.isFinite(asMs) && /^\d+$/.test(value)) {
    return asMs
  }

  if (!isValidTimeLabel(value)) {
    return null
  }

  return timeLabelToMs(value)
}

const applyTimeDraft = (
  draft: string,
  setInvalid: (value: boolean) => void,
  setTime: (value: number) => void,
) => {
  setInvalid(shouldShowTimeError(draft))

  if (!isValidTimeLabel(draft)) {
    return
  }

  setInvalid(false)
  setTime(timeLabelToMs(draft))
}

const timeFromLabel = computed({
  get: () => msToTimeLabel(timeFrom.value),
  set: (value: string | number) => {
    const parsed = parseComboboxValue(value)
    if (parsed === null) {
      isTimeFromInvalid.value = true
      return
    }
    isTimeFromInvalid.value = false
    timeFrom.value = parsed
  },
})

const timeToLabel = computed({
  get: () => msToTimeLabel(timeTo.value),
  set: (value: string | number) => {
    const parsed = parseComboboxValue(value)
    if (parsed === null) {
      isTimeToInvalid.value = true
      return
    }
    isTimeToInvalid.value = false
    timeTo.value = parsed
  },
})

const onTimeFromSearch = (value: string) => {
  applyTimeDraft(
    value,
    (invalid) => { isTimeFromInvalid.value = invalid },
    (ms) => { timeFrom.value = ms },
  )
}

const onTimeToSearch = (value: string) => {
  applyTimeDraft(
    value,
    (invalid) => { isTimeToInvalid.value = invalid },
    (ms) => { timeTo.value = ms },
  )
}

const timeFromOptions = computed(() => {
  if (!props.isTodaySelected) {
    return allTimesForSelect
  }

  const minOffset = getNextTimeSlotOffset(now.value, TIME_SLOT_STEP_MS)
  return allTimesForSelect.filter((item) => timeLabelToMs(String(item.value)) >= minOffset)
})

const timeToOptions = computed(() => (
  allTimesForSelect.filter((item) => timeLabelToMs(String(item.value)) !== timeFrom.value)
))

const applyDefaultsWhenUnset = () => {
  const bothZero = timeFrom.value === 0 && timeTo.value === 0
  const fromUnset = isScheduleTimeUnset(timeFrom.value) || bothZero
  const toUnset = isScheduleTimeUnset(timeTo.value) || bothZero
  if (!fromUnset && !toUnset) {
    return
  }

  const defaults = getDefaultScheduleTimeRange(now.value)

  if (fromUnset) {
    timeFrom.value = defaults.timeFrom
    if (toUnset) {
      timeTo.value = defaults.timeTo
    }
    return
  }

  if (toUnset) {
    timeTo.value = ensureTimeToDiffers(timeFrom.value, defaults.timeTo)
  }
}

const fixTime = async () => {
  await nextTick()
  applyDefaultsWhenUnset()
  if (isScheduleTimeUnset(timeFrom.value) || isScheduleTimeUnset(timeTo.value)) {
    return
  }

  timeTo.value = ensureTimeToDiffers(timeFrom.value, timeTo.value)
}

onBeforeMount(applyDefaultsWhenUnset)
onMounted(fixTime)
watch(() => props.isTodaySelected, fixTime)
watch([timeFrom, timeTo], ([nextFrom, nextTo], [previousFrom, previousTo]) => {
  if (isScheduleTimeUnset(nextFrom) || isScheduleTimeUnset(previousFrom)) {
    return
  }

  if (nextFrom === previousFrom || nextTo !== previousTo) {
    return
  }

  timeTo.value = shiftEndKeepingDuration(previousFrom, nextFrom, nextTo)
})
</script>

<style scoped>
/*
  Пустые подсказки («Нажмите Enter…» / «Нет опций…») рендерятся как
  .wui-combobox__list-item с cursor-default — прячем их и пустой дропдаун.
*/
.schedule-time-range :deep(.wui-combobox__list-item[class*='cursor-default']) {
  display: none !important;
}

.schedule-time-range :deep(.wui-combobox__list:not(:has(.wui-combobox__list-item:not([class*='cursor-default'])))) {
  display: none !important;
}
</style>
